import { Link } from 'react-router-dom';
import {
  bestFormat,
  displayTitle,
  isFullyLoanedOut,
  posterUrl,
  type DiscFormat,
  type Movie,
  type PosterSize,
} from '@physical-movies/core';
import { useI18n } from '../app/context';
import { useFormatLabels } from '../lib/labels';
import { Icon } from './Icon';
import styles from './poster.module.css';

export function FormatBadge({ format, long = false }: { format: DiscFormat; long?: boolean }) {
  const labels = useFormatLabels();
  return (
    <span className={styles.badge} data-format={format}>
      <span className={styles.badgeDot} data-format={format} aria-hidden="true" />
      {long ? labels.long(format) : labels.short(format)}
    </span>
  );
}

/** Artwork, or the title printed down a blank spine when there is none. */
export function PosterImage({
  posterPath,
  title,
  size = 'w342',
  eager = false,
}: {
  posterPath: string | null;
  title: string;
  size?: PosterSize;
  eager?: boolean;
}) {
  const url = posterUrl(posterPath, size);

  if (!url) {
    return (
      <div className={styles.fallback}>
        <span className={styles.fallbackTitle}>{title}</span>
      </div>
    );
  }

  return (
    <img
      className={styles.image}
      src={url}
      alt=""
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      draggable={false}
    />
  );
}

export function PosterCard({ movie, eager = false }: { movie: Movie; eager?: boolean }) {
  const { t, locale } = useI18n();
  const labels = useFormatLabels();

  const title = displayTitle(movie, locale);
  const format = bestFormat(movie);
  const lentOut = isFullyLoanedOut(movie);
  const cases = movie.copies.length;

  const meta = [
    movie.year ?? null,
    format ? labels.short(format) : null,
    cases > 1 ? `×${cases}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const played =
    movie.watchCount > 0
      ? t('movie.watchedTimes', { count: movie.watchCount })
      : t('movie.neverWatched');

  return (
    <Link
      to={`/movie/${movie.id}`}
      className={styles.card}
      aria-label={`${title}${movie.year ? `, ${movie.year}` : ''} — ${played}`}
    >
      <div className={`${styles.frame} ${lentOut ? styles.loanedOut : ''}`}>
        {format && <span className={styles.spine} data-format={format} aria-hidden="true" />}
        <PosterImage
          posterPath={movie.posterPath}
          title={title}
          size={eager ? 'w342' : 'w185'}
          eager={eager}
        />
        {lentOut && (
          <span className={styles.loanFlag} title={t('movie.unavailable')}>
            <Icon name="handoff" size={13} />
          </span>
        )}
        {movie.watchCount > 0 ? (
          <span className={styles.count} aria-hidden="true">
            {movie.watchCount}×
          </span>
        ) : (
          <span className={styles.ring} aria-hidden="true" />
        )}
      </div>
      <div className={styles.caption}>
        <span className={styles.title}>{title}</span>
        <span className={styles.meta}>{meta}</span>
      </div>
    </Link>
  );
}

export function PosterGrid({ movies }: { movies: Movie[] }) {
  return (
    <ul className={styles.grid}>
      {movies.map((movie, index) => (
        <li key={movie.id}>
          {/* The first row is above the fold on every screen size. */}
          <PosterCard movie={movie} eager={index < 6} />
        </li>
      ))}
    </ul>
  );
}
